/**
 * The OS keychain as the web layer sees it.
 *
 * What is stored is the **passphrase**, and the NIP-49 ciphertext stays exactly
 * where `identity/storage.ts` puts it. That is what makes this an integration
 * rather than a second key path: the desktop shell gains silent restore by
 * supplying the passphrase to the existing `unlock()`, and every rule about what
 * may be persisted, how a bunker's client key is handled, and which pubkey a
 * decrypted key must match is still enforced by the code that already enforces it.
 *
 * ## The index, and why one is needed
 *
 * There is no "list my entries" command — a webview that can enumerate the
 * keychain is most of the way to reading it. So this module keeps its own list of
 * the accounts it has stored something for. The list holds pubkeys, which are
 * public, and losing it costs nothing but an orphaned entry.
 *
 * It exists for one job: deleting a stored passphrase when the account it belongs
 * to is removed. Without it, signing out would leave the secret that unlocks a
 * deleted identity sitting in the OS keychain indefinitely, which on a shared
 * computer is the residue that matters most.
 */

import { invokeNative, isDesktopShell } from "./runtime";

/** Not a secret and not a capability: a list of public keys. */
const INDEX_KEY = "setu-desktop-keychain";

/** The account shape the Rust side accepts. Checked here so a bad call is never made. */
const HEX32 = /^[0-9a-f]{64}$/;

/**
 * A NIP-49 passphrase, generously bounded.
 *
 * Refused rather than truncated: a truncated passphrase is stored happily and then
 * fails every unlock, which presents as a broken keychain instead of a rejected
 * input.
 */
const MAX_PASSPHRASE_LENGTH = 512;

function readIndex(): string[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (value): value is string =>
            typeof value === "string" && HEX32.test(value),
        )
      : [];
  } catch {
    return [];
  }
}

function writeIndex(pubkeys: readonly string[]): void {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(pubkeys));
  } catch {
    // Storage unavailable. The keychain entry itself is unaffected; only the
    // bookkeeping that would later clean it up is lost.
  }
}

/** Accounts this device believes it has stored a passphrase for. */
export function keychainAccounts(): readonly string[] {
  return readIndex();
}

/** True when the passphrase for `pubkey` is stored on this device. */
export function hasStoredPassphrase(pubkey: string): boolean {
  return readIndex().includes(pubkey);
}

/**
 * Store the passphrase for an account. Resolves false if nothing was stored.
 *
 * The index is written only after the keychain accepted the secret, so a failed
 * write cannot leave a record claiming a secret exists — the reconciliation below
 * would then never clean up the entry that does exist, and a restore attempt would
 * wait on a secret that is not there.
 */
export async function storePassphrase(
  pubkey: string,
  passphrase: string,
): Promise<boolean> {
  if (!isDesktopShell()) return false;
  if (!HEX32.test(pubkey)) return false;
  if (passphrase.length === 0 || passphrase.length > MAX_PASSPHRASE_LENGTH) {
    return false;
  }

  const stored = await invokeNative<null>("remember_identity_passphrase", {
    pubkey,
    passphrase,
  });
  // `undefined` is the failure signal from `invokeNative`; the command itself
  // returns unit on success, which arrives as null.
  if (stored === undefined) return false;

  const index = readIndex();
  if (!index.includes(pubkey)) writeIndex([...index, pubkey]);
  return true;
}

/** The stored passphrase for an account, or undefined when there is none. */
export async function loadPassphrase(
  pubkey: string,
): Promise<string | undefined> {
  if (!isDesktopShell() || !HEX32.test(pubkey)) return undefined;
  const secret = await invokeNative<string | null>(
    "recall_identity_passphrase",
    { pubkey },
  );
  return typeof secret === "string" && secret.length > 0 ? secret : undefined;
}

/**
 * Delete the stored passphrase for an account.
 *
 * The index entry is dropped regardless of what the command reported. A retained
 * record of a secret we tried to delete is worse than an orphan: it would make
 * `hasStoredPassphrase` claim a silent restore is available when it is not.
 */
export async function dropPassphrase(pubkey: string): Promise<void> {
  if (!HEX32.test(pubkey)) return;
  const index = readIndex();
  if (index.includes(pubkey)) {
    writeIndex(index.filter((entry) => entry !== pubkey));
  }
  if (!isDesktopShell()) return;
  await invokeNative<null>("forget_identity_passphrase", { pubkey });
}

/**
 * Delete every stored passphrase whose account this device no longer remembers.
 *
 * Called with the live account list. This is how sign-out and "remove account"
 * reach the keychain without `SessionProvider` having to know a keychain exists:
 * both drop the credential record, and the passphrase follows on the next pass.
 */
export async function reconcileKeychain(
  accounts: readonly string[],
): Promise<void> {
  if (!isDesktopShell()) return;
  const keep = new Set(accounts);
  for (const pubkey of readIndex()) {
    if (!keep.has(pubkey)) await dropPassphrase(pubkey);
  }
}
